import { tableSortFilter } from '@/utils/arrayProcessing';
//
import type { DayOff } from '../types';
import type { ProviderJob } from '@/features/users/providers/types/provider-jobs';
import type { Review } from '@/features/reviews/types';
import type { User } from '@/features/users/shared/types';
import type { OffDaysRequestType } from '@/types';
//
import type { ProviderInvoice } from '@/features/invoices/types';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import { getReviewUserFullName } from '@/features/reviews/utils';

type ProviderJobServiceFilter = {
  id: number;
  variants: number[];
};

// PROVIDERS
export function applySortFilterProviders({
  tableData,
  comparator,
  filterName,
  filterStatus,
  filterCity,
}: {
  tableData: User[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string;
  filterCity: number;
}) {
  tableData = tableSortFilter(tableData, comparator);

  if (filterName) {
    tableData = tableData.filter((item) => {
      const name = `${item.first_name} ${item.last_name}`;
      return (
        name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        item.email.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
      );
    });
  }
  if (filterStatus && filterStatus !== 'all') {
    tableData = tableData.filter((item) =>
      !item.user_statuses
        ? false
        : (item.user_statuses.display ?? '').toLowerCase() === filterStatus
    );
  }

  if (filterCity !== 0) {
    tableData = tableData.filter((user) => {
      const currentAddress = user.address[0];
      return currentAddress ? currentAddress.city_id === filterCity : false;
    });
  }

  return tableData;
}

// REVIEWS
export function applySortFilterReviews({
  tableData,
  comparator,
  searchFieldValue,
}: {
  tableData: Review[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
}) {
  tableData = tableSortFilter(tableData, comparator);
  const searchFieldLowerCase = searchFieldValue.toLowerCase();

  if (searchFieldValue) {
    tableData = tableData.filter((item) => {
      const clientFullName = getReviewUserFullName(item.client);
      const cleanerFullName = getReviewUserFullName(item.cleaner);
      return (
        item.id.toString().indexOf(searchFieldValue) !== -1 ||
        clientFullName.toLowerCase().indexOf(searchFieldLowerCase) !== -1 ||
        cleanerFullName.toLowerCase().indexOf(searchFieldLowerCase) !== -1 ||
        (item.review ?? '').toLowerCase().indexOf(searchFieldLowerCase) !==
          -1 ||
        item.rating.toString().indexOf(searchFieldValue) !== -1
      );
    });
  }

  return tableData;
}

// CLEANER JOBS
const getProviderJobStatusKey = (job: ProviderJob) =>
  (
    job.booking_session_status?.value ??
    job.booking_session_status?.display ??
    job.session_status?.value ??
    job.session_status?.display ??
    job.booking_status?.value ??
    job.booking_status?.display ??
    ''
  ).toLowerCase();

const isCancelledProviderJob = (job: ProviderJob) => {
  const status = getProviderJobStatusKey(job);
  return status === 'cancelled' || status === 'canceled';
};

const getProviderJobDateString = (job: ProviderJob) => job.date?.slice(0, 10);

const getStatusId = (
  status:
    | ProviderJob['booking_session_status']
    | ProviderJob['session_status']
    | ProviderJob['booking_status']
) => (status as { id?: number } | null | undefined)?.id ?? 0;

const getProviderJobStatusId = (job: ProviderJob) =>
  job.status_id ||
  getStatusId(job.booking_session_status) ||
  getStatusId(job.session_status) ||
  getStatusId(job.booking_status) ||
  0;

const getProviderJobPaymentStatusId = (job: ProviderJob) =>
  job.payment_status_id || getStatusId(job.booking_session_payment_status) || 0;

const getProviderJobServiceVariantId = (job: ProviderJob) =>
  job.service_variant?.id ?? 0;

const getProviderJobServiceId = (job: ProviderJob) =>
  job.service_variant?.service?.id ?? 0;

const providerJobMatchesFilterStatus = (
  job: ProviderJob,
  filterStatus: string
) => {
  const normalizedFilter = filterStatus.toLowerCase();

  switch (normalizedFilter) {
    case 'previous':
    case 'today':
    case 'upcoming':
      return returnSessionFilterFromDate(job.date) === normalizedFilter;
    case 'unassigned':
      return (
        (job.booking_session_cleaners ?? job.booking_cleaners ?? []).length ===
        0
      );
    case 'cancelled':
      return isCancelledProviderJob(job);
    default:
      return true;
  }
};

const providerJobMatchesService = (
  job: ProviderJob,
  filterServiceId: number,
  servicesWithTheirVariants: ProviderJobServiceFilter[]
) => {
  if (filterServiceId === 0) return true;

  const directServiceId = getProviderJobServiceId(job);
  if (directServiceId !== 0) {
    return directServiceId === filterServiceId;
  }

  const selectedService = servicesWithTheirVariants.find(
    (service) => service.id === filterServiceId
  );
  return (
    selectedService?.variants.includes(getProviderJobServiceVariantId(job)) ??
    false
  );
};

const providerJobMatchesDateRange = (
  job: ProviderJob,
  filterStartDate: string | null,
  filterEndDate: string | null
) => {
  const jobDate = getProviderJobDateString(job);
  if (!jobDate) return false;

  if (filterStartDate && jobDate < filterStartDate) return false;
  if (filterEndDate && jobDate > filterEndDate) return false;

  return true;
};

export function applySortFilterProviderJobs({
  tableData,
  comparator,
  searchFieldValue,
  filterStatus = 'today',
  filterServiceId = 0,
  filterServiceTypeId = 0,
  filterStartDate = null,
  filterEndDate = null,
  filterStatusId = 0,
  filterPaymentStatusId = 0,
  servicesWithTheirVariants = [],
}: {
  tableData: ProviderJob[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
  filterStatus?: string;
  filterServiceId?: number;
  filterServiceTypeId?: number;
  filterStartDate?: string | null;
  filterEndDate?: string | null;
  filterStatusId?: number;
  filterPaymentStatusId?: number;
  servicesWithTheirVariants?: ProviderJobServiceFilter[];
}) {
  tableData = tableSortFilter(tableData, comparator);
  const searchFieldLowerCase = searchFieldValue.toLowerCase();

  if (searchFieldValue) {
    tableData = tableData.filter((item) => {
      const clientFullName = `${item.user?.first_name} ${item.user?.last_name}`;

      // Check if the search term matches the ID, client's full name, or email
      const matchesIdClientEmail =
        item.id.toString().indexOf(searchFieldValue) !== -1 ||
        clientFullName.toLowerCase().indexOf(searchFieldLowerCase) !== -1 ||
        item.user?.email.toLowerCase().indexOf(searchFieldLowerCase) !== -1;

      // Check if the search term matches any cleaner's first name or last name
      const cleaners =
        item.booking_session_cleaners ?? item.booking_cleaners ?? [];
      const matchesCleanerName = cleaners.some((cleaner) => {
        const cleanerFullName = `${cleaner.first_name} ${cleaner.last_name}`;
        return (
          cleanerFullName.toLowerCase().indexOf(searchFieldLowerCase) !== -1
        );
      });

      return matchesIdClientEmail || matchesCleanerName;
    });
  }

  if (filterStatus) {
    tableData = tableData.filter((item) =>
      providerJobMatchesFilterStatus(item, filterStatus)
    );
  }

  if (filterServiceId !== 0) {
    tableData = tableData.filter((item) =>
      providerJobMatchesService(
        item,
        filterServiceId,
        servicesWithTheirVariants
      )
    );
  }

  if (filterServiceTypeId !== 0) {
    tableData = tableData.filter(
      (item) => getProviderJobServiceVariantId(item) === filterServiceTypeId
    );
  }

  if (filterStartDate || filterEndDate) {
    tableData = tableData.filter((item) =>
      providerJobMatchesDateRange(item, filterStartDate, filterEndDate)
    );
  }

  if (filterStatusId !== 0) {
    tableData = tableData.filter(
      (item) => getProviderJobStatusId(item) === filterStatusId
    );
  }

  if (filterPaymentStatusId !== 0) {
    tableData = tableData.filter(
      (item) => getProviderJobPaymentStatusId(item) === filterPaymentStatusId
    );
  }

  return tableData;
}

// INVOICES
export function applySortFilterProviderInvoices({
  tableData,
  comparator,
  searchFieldValue,
}: {
  tableData: ProviderInvoice[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
}) {
  tableData = tableSortFilter(tableData, comparator);

  if (searchFieldValue) {
    tableData = tableData.filter(
      (item) => item.id.toString().indexOf(searchFieldValue) !== -1
    );
  }

  return tableData;
}

// CLEANER JOBS
export function applySortFilterProviderAvailability({
  tableData,
  comparator,
  searchFieldValue,
  availabilityTypeFilterValue,
  timeOffRequestTypes,
  filterStatus,
}: {
  tableData: DayOff[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
  availabilityTypeFilterValue: string;
  timeOffRequestTypes: OffDaysRequestType[];
  filterStatus: string;
}) {
  tableData = tableSortFilter(tableData, comparator);
  const searchFieldLowerCase = searchFieldValue.toLowerCase();

  if (searchFieldValue) {
    tableData = tableData.filter(
      (item) =>
        item.id.toString().indexOf(searchFieldValue) !== -1 ||
        String(item.notes).toLowerCase().indexOf(searchFieldLowerCase) !== -1
    );
  }

  if (availabilityTypeFilterValue !== 'all') {
    const currentTypeId = timeOffRequestTypes.find(
      (type) =>
        type.description.toLowerCase() ===
        availabilityTypeFilterValue.toLowerCase()
    );
    if (currentTypeId) {
      tableData = tableData.filter((item) => item.type_id === currentTypeId.id);
    }
  }
  if (filterStatus.toLowerCase() === 'expired') {
    tableData = tableData.filter((dayoff) => {
      if (dayoff.offday_recurrence) {
        if (!dayoff.offday_recurrence.until) return false;
        return new Date(dayoff.offday_recurrence.until) < new Date();
      }
      const expiryDate = dayoff.end_date ?? dayoff.start_date;
      if (!expiryDate) return false;
      return new Date(expiryDate) < new Date();
    });
  }
  if (filterStatus.toLowerCase() === 'active') {
    tableData = tableData.filter((dayoff) => {
      if (dayoff.offday_recurrence) {
        if (!dayoff.offday_recurrence.until) return true;
        return new Date(dayoff.offday_recurrence.until) >= new Date();
      }
      const expiryDate = dayoff.end_date ?? dayoff.start_date;
      if (!expiryDate) return false;
      return new Date(expiryDate) >= new Date();
    });
  }
  return tableData;
}
